import { Button } from "antd";
import { toast } from "sonner";
import i18n from "@/utils/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ObjectiveType,
  ObjectiveCompletionStatus,
} from "../../../types/client-education-detail.types";
import type {
  ChapterDto,
  ObjectiveDto,
  ObjectiveVideoQuestion,
  VirtualClassroomDto,
} from "../../../types/client-education-detail.types";
import {
  getFileStreamUrl,
  getFileDownloadUrl,
} from "../../../../education-catalog/services/education-catalog.api";
import { JitsiEmbed, extractJitsiUserFromToken } from "./JitsiEmbed";
import { clientEducationDetailApi } from "../../../services/client-education-detail.api";
import { getJitsiAvailability } from "../../../utils/classroom-availability";
import { findClassroomForJitsiUrl } from "../../../utils/jitsi";
import { useClassroomPresence } from "../../../hooks/useClassroomPresence";
import dayjs from "dayjs";
import styles from "./ContentSection.module.scss";

function parseQuestionSeconds(q: ObjectiveVideoQuestion): number {
  if (typeof q.triggerSeconds === "number") return q.triggerSeconds;
  const ts = q.timestamp;
  if (!ts) return 0;
  const parts = ts.split(":").map((p) => parseInt(p, 10));
  if (parts.some((p) => Number.isNaN(p))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] ?? 0;
}

interface Props {
  objective: ObjectiveDto | null;
  chapter?: ChapterDto | null;
  enrollmentId: string;
  virtualClassrooms?: VirtualClassroomDto[] | null;
  onMarkComplete: (
    objective: ObjectiveDto,
    opts?: { watchedSeconds?: number; lastPositionSeconds?: number }
  ) => Promise<void>;
  onProgress: (
    objective: ObjectiveDto,
    state: {
      watchedSeconds: number;
      lastPositionSeconds: number;
      progressPercent: number;
      watchedSecondsDelta?: number;
      segmentStartSeconds?: number;
      durationSeconds?: number;
    },
    opts?: { force?: boolean }
  ) => Promise<boolean>;
  onRequestRefresh?: () => void | Promise<void>;
  onPrev?: () => void;
  onNext?: () => void;
}

export function InlinePlayer(props: Props) {
  const {
    objective,
    chapter,
    enrollmentId: _enrollmentId,
    virtualClassrooms,
    onMarkComplete,
    onProgress,
    onRequestRefresh,
    onPrev,
    onNext,
  } = props;
  const seekUnlocked = (chapter?.progressPercentage ?? 0) >= 100;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const completedIdsRef = useRef<Set<string>>(new Set());
  const [completing, setCompleting] = useState(false);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [streamError, setStreamError] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState(false);
  const maxWatchedRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const pendingWatchedDeltaRef = useRef<number>(0);
  // Henüz raporlanmamış kesintisiz izleme aralığının başlangıcı (saniye). Backend
  // izlenen süreyi tekil aralıklardan hesaplar; sarma aralığı böler.
  const segmentStartRef = useRef<number | null>(null);
  const progressSavingRef = useRef(false);
  const resumeSecondRef = useRef<number>(0);
  const SEEK_TOLERANCE = 1.5;
  const [activeQuestion, setActiveQuestion] =
    useState<ObjectiveVideoQuestion | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [answerFeedback, setAnswerFeedback] = useState<
    "correct" | "incorrect" | "error" | null
  >(null);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  // Yalnız sunucunun DOĞRU onayladığı sorular (pentest 3.3.1: doğru cevap
  // istemciye gelmez, kontrol backend'de).
  const answeredQuestionsRef = useRef<Set<string>>(new Set());

  const sortedQuestions = useMemo(() => {
    const qs = (objective?.videoQuestions ?? []).slice();
    return qs
      .map((q) => ({ q, t: parseQuestionSeconds(q) }))
      .sort((a, b) => a.t - b.t);
  }, [objective?.id, objective?.videoQuestions]);

  useEffect(() => {
    answeredQuestionsRef.current = new Set();
    setActiveQuestion(null);
    setSelectedOptionId(null);
    setAnswerFeedback(null);
    setSubmittingAnswer(false);
    // Daha önce doğru cevaplanan sorular yenilemeden sonra tekrar çıkmasın:
    // cevap durumlarını sunucudan al (doğru seçenek bilgisi içermez).
    const objectiveId = objective?.id;
    if (!objectiveId || !(objective?.videoQuestions?.length ?? 0)) return;
    let cancelled = false;
    clientEducationDetailApi
      .getVideoQuestionAnswers(objectiveId)
      .then((rows) => {
        if (cancelled) return;
        for (const row of rows) {
          if (row.answeredCorrectly) answeredQuestionsRef.current.add(row.questionId);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [objective?.id]);

  useEffect(() => {
    if (!objective || objective.type !== ObjectiveType.INTERNAL_FILE) return;
    const video = videoRef.current;
    if (!video) return;
    const resumeSecond =
      resumeSecondRef.current ||
      objective.currentSecond ||
      objective.lastPositionSeconds ||
      0;
    maxWatchedRef.current = seekUnlocked
      ? Number.POSITIVE_INFINITY
      : resumeSecond;
    lastTimeRef.current = resumeSecond;
    segmentStartRef.current = null;
    video.currentTime = resumeSecond;
  }, [objective?.id, streamUrl, seekUnlocked]);

  useEffect(() => {
    resumeSecondRef.current =
      objective?.currentSecond || objective?.lastPositionSeconds || 0;
    pendingWatchedDeltaRef.current = 0;
    segmentStartRef.current = null;
  }, [objective?.id, objective?.currentSecond, objective?.lastPositionSeconds]);

  useEffect(() => {
    const isVid =
      objective?.type === ObjectiveType.INTERNAL_FILE &&
      (objective?.file?.mimeType?.startsWith("video") ?? false);
    if (!objective || !isVid || objective.isLocked) return;
    let cancelled = false;
    clientEducationDetailApi
      .getObjectiveVideoWatch(objective.id)
      .then((watch) => {
        if (cancelled) return;
        const resumeSecond =
          watch.currentSecond ?? watch.lastPositionSeconds ?? 0;
        resumeSecondRef.current = resumeSecond;
        const video = videoRef.current;
        if (video) {
          video.currentTime = resumeSecond;
          maxWatchedRef.current = seekUnlocked
            ? Number.POSITIVE_INFINITY
            : resumeSecond;
          lastTimeRef.current = resumeSecond;
          segmentStartRef.current = null;
        }
      })
      .catch((error) => {
        const message = (error as { response?: { data?: { message?: string } } })
          ?.response?.data?.message;
        if (
          message ===
          "This video cannot be started before the previous video is completed"
        ) {
          toast.error(i18n.t("inlinePlayer.lockedPreviousRequired"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [objective?.id, objective?.type, objective?.file?.mimeType, objective?.isLocked, seekUnlocked]);

  useEffect(() => {
    setStreamUrl(null);
    setStreamError(false);
    const fileId = objective?.fileID ?? objective?.file?.id;
    const isVid =
      objective?.type === ObjectiveType.INTERNAL_FILE &&
      (objective?.file?.mimeType?.startsWith("video") ?? false);
    if (!isVid || !fileId || objective?.isLocked) return;
    let revoked = false;
    let created: string | null = null;
    getFileStreamUrl(fileId)
      .then((url) => {
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setStreamUrl(url);
      })
      .catch(() => setStreamError(true));
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [objective?.id, objective?.fileID, objective?.file?.id, objective?.type, objective?.file?.mimeType]);

  useEffect(() => {
    setDownloadUrl(null);
    setDownloadError(false);
    const fileId = objective?.fileID ?? objective?.file?.id;
    const mime = objective?.file?.mimeType ?? "";
    const isFile = objective?.type === ObjectiveType.INTERNAL_FILE;
    const isDownloadable = isFile && !mime.startsWith("video");
    if (!isDownloadable || !fileId) return;
    let cancelled = false;
    getFileDownloadUrl(fileId)
      .then((url) => {
        if (!cancelled) setDownloadUrl(url);
      })
      .catch(() => {
        if (!cancelled) setDownloadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [objective?.id, objective?.fileID, objective?.file?.id, objective?.type, objective?.file?.mimeType]);

  useEffect(() => {
    // Sekme gizlendiğinde durdur (tab switch)
    function handleVisibilityChange() {
      if (document.hidden) {
        const v = videoRef.current;
        if (v && !v.paused) v.pause();
      }
    }
    // Pencere odak kaybettiğinde durdur (alt-tab, başka uygulama).
    // window.blur document.hidden kontrolü yapmaz çünkü Chrome'da blur
    // sırasında hidden=false olur; burada koşulsuz pause gerekiyor.
    function handleWindowBlur() {
      const v = videoRef.current;
      if (v && !v.paused) v.pause();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, []);

  // İçerikteki Jitsi oturumu da canlı sınıf sayfası gibi giriş-çıkış kaydeder.
  const presenceClassroomId = useMemo(
    () =>
      objective?.type === ObjectiveType.JITSI
        ? (findClassroomForJitsiUrl(virtualClassrooms, objective.externalUrl)?.id ?? null)
        : null,
    [objective?.type, objective?.externalUrl, virtualClassrooms],
  );
  const { markJoined, markLeft } = useClassroomPresence(presenceClassroomId);

  useEffect(() => {
    function flushOnExit() {
      void flushProgress(true);
    }
    window.addEventListener("pagehide", flushOnExit);
    window.addEventListener("beforeunload", flushOnExit);
    return () => {
      window.removeEventListener("pagehide", flushOnExit);
      window.removeEventListener("beforeunload", flushOnExit);
    };
  }, [objective?.id]);

  if (!objective) {
    return (
      <div className={styles.playerEmpty}>
        Bir içerik seç.
      </div>
    );
  }

  const isVideo =
    objective.type === ObjectiveType.INTERNAL_FILE &&
    (objective.file?.mimeType?.startsWith("video") ?? false);
  const isDownloadableFile =
    objective.type === ObjectiveType.INTERNAL_FILE && !isVideo;
  const fileName = objective.file?.originalName ?? "";
  const mimeType = objective.file?.mimeType ?? "";
  const lowerName = fileName.toLowerCase();
  const isPdf =
    isDownloadableFile &&
    (mimeType.includes("pdf") || lowerName.endsWith(".pdf"));
  const isOfficeDoc =
    isDownloadableFile &&
    !isPdf &&
    (mimeType.includes("officedocument") ||
      mimeType.includes("msword") ||
      mimeType.includes("ms-excel") ||
      mimeType.includes("ms-powerpoint") ||
      /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf)$/i.test(lowerName));
  const officeViewerUrl =
    isOfficeDoc && downloadUrl
      ? `https://docs.google.com/gview?url=${encodeURIComponent(downloadUrl)}&embedded=true`
      : null;
  const previewUrl = isPdf
    ? downloadUrl
    : isOfficeDoc
      ? officeViewerUrl
      : downloadUrl;
  const isLink = objective.type === ObjectiveType.LINK;
  const isJitsi = objective.type === ObjectiveType.JITSI;
  const jitsiAvailability = isJitsi
    ? getJitsiAvailability(virtualClassrooms)
    : null;

  /**
   * Birikmiş izleme süresini raporlar.
   *
   * @param endPosition Aralığın bitişi; verilmezse o anki pozisyon. Sarma anında
   *   çağrıldığında sarmadan önceki pozisyon verilir.
   */
  async function flushProgress(force = false, endPosition?: number) {
    if (!objective || progressSavingRef.current) return;
    const v = videoRef.current;
    if (!v?.duration) return;
    const delta = Math.floor(pendingWatchedDeltaRef.current);
    if (delta <= 0) return;
    // await sırasında oynatma sürer ya da kullanıcı sarar; aralığı şimdi sabitle.
    const segmentEnd = endPosition ?? v.currentTime;
    const segmentStart = segmentStartRef.current;
    progressSavingRef.current = true;
    try {
      const sent = await onProgress(
        objective,
        {
          watchedSeconds: Math.floor(maxWatchedRef.current),
          lastPositionSeconds: Math.floor(segmentEnd),
          progressPercent: Math.min(
            100,
            Math.round((segmentEnd / v.duration) * 100),
          ),
          watchedSecondsDelta: delta,
          segmentStartSeconds:
            segmentStart != null ? Math.floor(segmentStart) : undefined,
          durationSeconds: Math.round(v.duration),
        },
        { force },
      );
      if (sent) {
        pendingWatchedDeltaRef.current = Math.max(
          0,
          pendingWatchedDeltaRef.current - delta,
        );
        // Arada sarma olmadıysa sonraki aralık gönderilen bitişten devam eder.
        if (segmentStartRef.current === segmentStart) {
          segmentStartRef.current = segmentEnd;
        }
      }
    } finally {
      progressSavingRef.current = false;
    }
  }

  /** Kesintisiz oynatmayı mevcut aralığa ekler. */
  function extendSegment(delta: number) {
    if (segmentStartRef.current === null) {
      segmentStartRef.current = lastTimeRef.current;
    }
    pendingWatchedDeltaRef.current += delta;
  }

  /** Sarma: birikeni sarmadan önceki pozisyonla raporla, yeni aralık başlat. */
  function breakSegment() {
    if (pendingWatchedDeltaRef.current >= 1) {
      void flushProgress(true, lastTimeRef.current);
    }
    segmentStartRef.current = null;
  }

  function handleTimeUpdate(e: React.SyntheticEvent<HTMLVideoElement>) {
    const v = e.currentTarget;
    if (!v.duration) return;
    const delta = v.currentTime - lastTimeRef.current;
    const continuous = delta >= 0 && delta <= SEEK_TOLERANCE;
    if (!seekUnlocked) {
      if (v.seeking) {
        if (!continuous) breakSegment();
        if (v.currentTime > maxWatchedRef.current + SEEK_TOLERANCE) {
          v.currentTime = maxWatchedRef.current;
        }
      } else if (continuous) {
        extendSegment(delta);
        if (v.currentTime > maxWatchedRef.current) {
          maxWatchedRef.current = v.currentTime;
        }
      } else {
        breakSegment();
        if (v.currentTime > maxWatchedRef.current + SEEK_TOLERANCE) {
          v.currentTime = maxWatchedRef.current;
        }
      }
    } else {
      if (continuous) {
        extendSegment(delta);
      } else {
        breakSegment();
      }
      if (v.currentTime > maxWatchedRef.current) {
        maxWatchedRef.current = v.currentTime;
      }
    }
    lastTimeRef.current = v.currentTime;

    if (!activeQuestion && !v.paused) {
      if (!seekUnlocked) {
        const trigger = sortedQuestions.find(
          ({ q, t }) =>
            v.currentTime >= t &&
            v.currentTime < t + 1.5 &&
            !answeredQuestionsRef.current.has(q.id),
        );
        if (trigger) {
          v.pause();
          void flushProgress(true);
          if (document.fullscreenElement) {
            void document.exitFullscreen().catch(() => undefined);
          }
          setActiveQuestion(trigger.q);
          setSelectedOptionId(null);
          setAnswerFeedback(null);
          return;
        }
      }
    }
    void flushProgress(false);
  }

  /** Henüz doğru cevaplanmamış ilk soru (videonun sonunda kaçırılan sorular için). */
  function firstUnansweredQuestion(): ObjectiveVideoQuestion | null {
    return (
      sortedQuestions.find(({ q }) => !answeredQuestionsRef.current.has(q.id))
        ?.q ?? null
    );
  }

  /**
   * Cevabı sunucuda kontrol ettirir (pentest 3.3.1). Yanlışta doğru seçenek
   * açıklanmaz; kursiyer tekrar dener. Yalnız doğru cevapta soru "cevaplandı"
   * sayılır ve video devam eder.
   */
  async function submitAnswer() {
    if (!objective || !activeQuestion || !selectedOptionId || submittingAnswer) return;
    const question = activeQuestion;
    setSubmittingAnswer(true);
    try {
      const res = await clientEducationDetailApi.answerVideoQuestion(
        objective.id,
        question.id,
        selectedOptionId,
      );
      if (!res.isCorrect) {
        setAnswerFeedback("incorrect");
        return;
      }
      setAnswerFeedback("correct");
      answeredQuestionsRef.current.add(question.id);
      const videoEnded = videoRef.current?.ended ?? false;
      const next = videoEnded ? firstUnansweredQuestion() : null;
      if (videoEnded && !next) {
        // Video zaten bitmişti; son soru da doğru — backend objective'i tamamladı.
        void onRequestRefresh?.();
      }
      window.setTimeout(() => {
        setAnswerFeedback(null);
        setSelectedOptionId(null);
        if (next) {
          setActiveQuestion(next);
          return;
        }
        setActiveQuestion(null);
        if (!videoEnded) void videoRef.current?.play().catch(() => undefined);
      }, 700);
    } catch (error) {
      // Soru (veya objective) bu sayfa açıkken düzenlenip yeniden oluşturulduysa eski
      // soru ID'si 404 alır: tekrar denemek işe yaramaz — soruyu kapat ve içeriği yenile.
      if ((error as { response?: { status?: number } })?.response?.status === 404) {
        setAnswerFeedback(null);
        setSelectedOptionId(null);
        setActiveQuestion(null);
        void onRequestRefresh?.();
        return;
      }
      setAnswerFeedback("error");
    } finally {
      setSubmittingAnswer(false);
    }
  }

  function handleSeeking(e: React.SyntheticEvent<HTMLVideoElement>) {
    const v = e.currentTarget;
    if (Math.abs(v.currentTime - lastTimeRef.current) > SEEK_TOLERANCE) {
      breakSegment();
    }
    if (seekUnlocked) return;
    if (v.currentTime > maxWatchedRef.current + SEEK_TOLERANCE) {
      v.currentTime = maxWatchedRef.current;
    }
  }

  async function handleEnded() {
    if (!objective) return;
    const v = videoRef.current;
    const duration = Math.floor(v?.duration ?? 0);
    const alreadyCompleted =
      (objective.completionStatus ?? objective.status) === ObjectiveCompletionStatus.COMPLETED ||
      completedIdsRef.current.has(objective.id);
    if (!alreadyCompleted && duration > 0 && v) {
      const delta = Math.max(0, duration - Math.floor(lastTimeRef.current));
      if (segmentStartRef.current === null) {
        segmentStartRef.current = lastTimeRef.current;
      }
      pendingWatchedDeltaRef.current += delta;
      lastTimeRef.current = duration;
      maxWatchedRef.current = duration;
      completedIdsRef.current.add(objective.id);
      await flushProgress(true);
      maxWatchedRef.current = Number.POSITIVE_INFINITY;
    }
    if (onRequestRefresh) {
      await onRequestRefresh();
    }
    // Video bitti ama cevaplanmamış soru kaldıysa (ör. sorunun anı kaçtı):
    // objective soru cevaplanmadan tamamlanmaz, soruyu şimdi sor.
    if (!alreadyCompleted && !activeQuestion) {
      const pending = firstUnansweredQuestion();
      if (pending) {
        if (document.fullscreenElement) {
          void document.exitFullscreen().catch(() => undefined);
        }
        setActiveQuestion(pending);
        setSelectedOptionId(null);
        setAnswerFeedback(null);
      }
    }
  }

  return (
    <div className={styles.player}>
      <div className={styles.playerHeader}>
        <h3>{objective.title}</h3>
        <div className={styles.playerNav}>
          <Button onClick={onPrev} disabled={!onPrev}>← {i18n.t("common.previous")}</Button>
          <Button onClick={onNext} disabled={!onNext}>Sonraki →</Button>
        </div>
      </div>

      {isVideo && objective.isLocked && (
        <div className={styles.playerEmpty}>
          {i18n.t("inlinePlayer.lockedPreviousRequired")}
        </div>
      )}

      {isVideo && !objective.isLocked && streamUrl && (
        <>
          <video
            ref={videoRef}
            src={streamUrl}
            controls
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            className={styles.playerVideo}
            onTimeUpdate={handleTimeUpdate}
            onSeeking={handleSeeking}
            onSeeked={handleSeeking}
            onEnded={handleEnded}
            onPause={() => void flushProgress(true)}
          />
          {!seekUnlocked && (
            <div style={{ marginTop: 8, fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
              İlk izlemeyi bitirdikten sonra videoyu istediğin yerden oynatabilirsin.
            </div>
          )}
        </>
      )}

      {isVideo && !objective.isLocked && !streamUrl && !streamError && (
        <div className={styles.playerEmpty}>{i18n.t("inlinePlayer.videoLoading")}</div>
      )}

      {isVideo && streamError && (
        <div className={styles.playerEmpty}>{i18n.t("inlinePlayer.videoLoadError")}</div>
      )}

      {isDownloadableFile && downloadUrl && (
        <>
          <iframe
            title={objective.title}
            src={previewUrl ?? downloadUrl}
            className={styles.playerPdf}
          />
          <div style={{ marginTop: 8 }}>
            <a href={downloadUrl} target="_blank" rel="noreferrer">
              <Button>
                {isPdf
                  ? "PDF'i indir"
                  : isOfficeDoc
                    ? i18n.t("inlinePlayer.downloadDocument")
                    : i18n.t("inlinePlayer.downloadFile")}
              </Button>
            </a>
          </div>
        </>
      )}

      {isDownloadableFile && !downloadUrl && !downloadError && (
        <div className={styles.playerEmpty}>{i18n.t("inlinePlayer.fileLoading")}</div>
      )}

      {isDownloadableFile && downloadError && (
        <div className={styles.playerEmpty}>{i18n.t("inlinePlayer.fileLoadError")}</div>
      )}

      {isLink && objective.externalUrl && (
        <div className={styles.playerLink}>
          <p>{i18n.t("inlinePlayer.externalLink")}</p>
          <a href={objective.externalUrl} target="_blank" rel="noreferrer">
            <Button type="primary">{i18n.t("inlinePlayer.openLink")}</Button>
          </a>
        </div>
      )}

      {isJitsi && objective.externalUrl && jitsiAvailability?.canJoin && (
        <JitsiEmbed
          externalUrl={objective.externalUrl}
          token={objective.token}
          user={extractJitsiUserFromToken(objective.token)}
          onJoin={markJoined}
          onLeave={markLeft}
          className={styles.playerJitsi}
        />
      )}

      {isJitsi && objective.externalUrl && !jitsiAvailability?.canJoin && (
        <div className={styles.playerLink}>
          <p>
            {jitsiAvailability?.nextStart != null
              ? i18n.t("inlinePlayer.jitsiNotStarted", {
                  when: dayjs(jitsiAvailability.nextStart).format(
                    "DD MMM YYYY HH:mm",
                  ),
                })
              : i18n.t("inlinePlayer.jitsiUnscheduled")}
          </p>
        </div>
      )}

      {isJitsi && !objective.externalUrl && (
        <div className={styles.playerLink}>
          <p>{i18n.t("inlinePlayer.jitsiNotReady")}</p>
        </div>
      )}

      {/* Canlı (Jitsi) oturumlarda manuel "tamamla" yok — tamamlanma katılım/
          yoklama üzerinden işlenir; kursiyer bu butonla kendini tamamlayamamalı. */}
      {(objective.completionStatus ?? objective.status) !== ObjectiveCompletionStatus.COMPLETED && !isVideo && !isJitsi && (
        <div style={{ marginTop: 12 }}>
          <Button
            type="primary"
            loading={completing}
            onClick={async () => {
              setCompleting(true);
              try {
                await onMarkComplete(objective);
              } finally {
                setCompleting(false);
              }
            }}
          >
            Tamamlandı işaretle
          </Button>
        </div>
      )}

      {activeQuestion && (
        <div className={styles.questionOverlay} role="dialog" aria-modal="true">
          <div className={styles.questionCard}>
            <h4>{activeQuestion.questionText ?? activeQuestion.question}</h4>
            <div className={styles.questionOptions}>
              {(activeQuestion.options ?? []).map((opt) => {
                const isSelected = selectedOptionId === opt.id;
                return (
                  <label
                    key={opt.id}
                    className={`${styles.questionOption} ${
                      isSelected ? styles.questionOptionSelected : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="video-question"
                      checked={isSelected}
                      onChange={() => {
                        setSelectedOptionId(opt.id);
                        setAnswerFeedback(null);
                      }}
                    />
                    <span>{opt.optionText ?? opt.text}</span>
                  </label>
                );
              })}
            </div>
            {answerFeedback === "incorrect" && (
              <p className={styles.questionError}>
                {i18n.t("inlinePlayer.wrongTryAgain", {
                  defaultValue: "Yanlış cevap, tekrar deneyin.",
                })}
              </p>
            )}
            {answerFeedback === "error" && (
              <p className={styles.questionError}>
                {i18n.t("inlinePlayer.answerError", {
                  defaultValue: "Cevap gönderilemedi. Lütfen tekrar deneyin.",
                })}
              </p>
            )}
            {answerFeedback === "correct" && (
              <p className={styles.questionOk}>{i18n.t("inlinePlayer.correctContinue")}</p>
            )}
            <div className={styles.questionActions}>
              <Button
                type="primary"
                loading={submittingAnswer}
                disabled={!selectedOptionId || answerFeedback === "correct"}
                onClick={() => void submitAnswer()}
              >
                {submittingAnswer
                  ? i18n.t("inlinePlayer.answerChecking", {
                      defaultValue: "Cevap kontrol ediliyor…",
                    })
                  : i18n.t("inlinePlayer.submitAnswer", { defaultValue: "Cevapla" })}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
